
const KEY_CONTEXT = "context";
const KEY_CONSTANT = "constant";
const KEY_CONTEXT_VALUE = "contextValue";

const VALID_SOURCES = [KEY_CONTEXT, KEY_CONSTANT];
const { PRIMITIVE_CONTEXT_TYPES: VALID_VALUE_TYPES } = require("../helpers/required-context-entity-type");

class RequiredContextValue {


  static isPrimitiveBinding(value) {
    return Boolean(value)
      && typeof value === "object"
      && !Array.isArray(value)
      && VALID_VALUE_TYPES.includes(value.valueType);
  }

  static isEntityBinding(value) {
    return Boolean(value)
      && typeof value === "object"
      && !Array.isArray(value)
      && !RequiredContextValue.isPrimitiveBinding(value)
      && typeof value.entityType === "string"
      && value.entityType !== ""
      && VALID_SOURCES.includes(value.entitySource);
  }

  static normalize(binding) {
    if (!RequiredContextValue.isPrimitiveBinding(binding)) {
      return null;
    }
    const isDirectValueBind = binding.entitySource === KEY_CONTEXT && Boolean(binding.sourceContext) && !binding.sourceField;
    return {
      valueType: binding.valueType,
      entitySource: isDirectValueBind
        ? KEY_CONTEXT_VALUE
        : VALID_SOURCES.includes(binding.entitySource) ? binding.entitySource : null,
      sourceContext: binding.sourceContext ?? null,
      sourceField: binding.sourceField ?? null,
      value: Object.prototype.hasOwnProperty.call(binding, "value") ? binding.value : null,
    };
  }

  static build(formState) {
    const built = { valueType: formState.valueType };
    if (formState.entitySource === KEY_CONSTANT) {
      built.entitySource = KEY_CONSTANT;
      built.value = RequiredContextValue.coerce(formState.value, formState.valueType);
      return built;
    }
    built.entitySource = KEY_CONTEXT;
    built.sourceContext = formState.sourceContext;
    if (formState.entitySource === KEY_CONTEXT_VALUE) {
      return built;
    }
    built.sourceField = formState.sourceField;
    return built;
  }

  static isComplete(formState) {
    if (!formState || !VALID_VALUE_TYPES.includes(formState.valueType)) {
      return false;
    }
    if (formState.entitySource === KEY_CONSTANT) {
      const coerced = RequiredContextValue.coerce(formState.value, formState.valueType);
      return coerced !== undefined && coerced !== "";
    }
    if (formState.entitySource === KEY_CONTEXT) {
      return Boolean(formState.sourceContext) && Boolean(formState.sourceField);
    }
    if (formState.entitySource === KEY_CONTEXT_VALUE) {
      return Boolean(formState.sourceContext);
    }
    return false;
  }

  static coerce(raw, valueType) {
    if (raw === undefined || raw === null) {
      return undefined;
    }
    if (valueType === "string") {
      return typeof raw === "string" ? raw : String(raw);
    }
    if (valueType === "number") {
      if (raw === "") {
        return undefined;
      }
      const asNumber = typeof raw === "number" ? raw : Number(raw);
      return Number.isFinite(asNumber) ? asNumber : undefined;
    }
    if (valueType === "boolean") {
      if (typeof raw === "boolean") {
        return raw;
      }
      if (raw === "true" || raw === "1" || raw === 1) {
        return true;
      }
      if (raw === "false" || raw === "0" || raw === 0) {
        return false;
      }
      return undefined;
    }
    return undefined;
  }

  static resolve(binding, { contexts = {}, readEntityField } = {}) {
    const normalized = RequiredContextValue.normalize(binding);
    if (!normalized) {
      return { reason: "not a primitive context binding" };
    }
    if (normalized.entitySource === KEY_CONSTANT) {
      const coerced = RequiredContextValue.coerce(normalized.value, normalized.valueType);
      return coerced === undefined
        ? { reason: `the constant is not a valid ${normalized.valueType}` }
        : { value: coerced };
    }
    if (normalized.entitySource === KEY_CONTEXT_VALUE) {
      return RequiredContextValue.#resolveDirectValue(normalized, contexts);
    }
    if (normalized.entitySource !== KEY_CONTEXT) {
      return { reason: "the binding names no source" };
    }
    const { sourceContext, sourceField, valueType } = normalized;
    if (!sourceContext || !sourceField) {
      return { reason: "the binding is incomplete" };
    }
    const ctx = contexts[sourceContext];
    if (!ctx?.entityType || !ctx.entityPkUrlVars) {
      return { reason: `no "${sourceContext}" context is in scope here` };
    }
    if (typeof readEntityField !== "function") {
      return { reason: "no entity-field reader was supplied" };
    }
    const raw = readEntityField(ctx.entityType, ctx.entityPkUrlVars, sourceField, ctx.appName ?? null);
    if (raw === undefined || raw === null) {
      return { reason: `the "${sourceContext}" context has no "${sourceField}" yet` };
    }
    const coerced = RequiredContextValue.coerce(raw, valueType);
    return coerced === undefined
      ? { reason: `"${sourceContext}.${sourceField}" is not a ${valueType}` }
      : { value: coerced };
  }

  static #resolveDirectValue({ sourceContext, valueType }, contexts) {
    const raw = contexts[sourceContext];
    if (raw === undefined || raw === null) {
      return { reason: `no "${sourceContext}" value is in scope here` };
    }
    if (typeof raw === "object") {
      return { reason: `"${sourceContext}" is an entity, not a ${valueType}` };
    }
    const coerced = RequiredContextValue.coerce(raw, valueType);
    return coerced === undefined
      ? { reason: `"${sourceContext}" is not a ${valueType}` }
      : { value: coerced };
  }
}

RequiredContextValue.KEY_CONTEXT = KEY_CONTEXT;
RequiredContextValue.KEY_CONTEXT_VALUE = KEY_CONTEXT_VALUE;
RequiredContextValue.KEY_CONSTANT = KEY_CONSTANT;
RequiredContextValue.VALID_VALUE_TYPES = VALID_VALUE_TYPES;

module.exports = RequiredContextValue;
